<template>
  <section class="dp-layout tr-page prep-config-page">
    <section class="dp-left">
      <DepartmentPane directory-scope="school" @tree-node-click="treeNodeClick" :type="1"></DepartmentPane>
    </section>
    <section class="dp-right config-page-right">
      <header class="tr-hero">
        <div>
          <p class="tr-eyebrow">集体备课 · 学校规则</p>
          <h1 class="tr-title">集体备课配置</h1>
          <p class="tr-sub">学校级默认规则，新建教研组时自动带入；组内仍可微调通过阈值与评语字数。</p>
        </div>
        <div class="tr-hero__meta">
          <div class="tr-stat">
            <span>当前学校</span>
            <strong class="tr-stat__school">{{ currentSchoolName || "未选择" }}</strong>
          </div>
        </div>
      </header>

      <div v-if="!schoolId" class="tr-empty">
        <div class="tr-empty__icon"><i class="el-icon-office-building"></i></div>
        <p class="tr-empty__title">尚未选择学校</p>
        <p class="tr-empty__desc">请从左侧机构树选择学校后再配置集体备课规则</p>
      </div>

      <div v-else class="tr-panel config-panel" v-loading="loading">
        <div class="card-head">
          <span class="card-head__title">投票与流程规则</span>
          <el-button
            type="primary"
            size="small"
            class="tr-btn-primary"
            icon="el-icon-check"
            :loading="saving"
            :disabled="!canSave"
            @click="saveConfig"
          >保存配置</el-button>
        </div>

        <div class="tr-rule-list">
          <div class="tr-rule-item">
            <div class="tr-rule-main">
              <div class="tr-rule-title">同意达到 N%</div>
              <div class="tr-rule-tip">同意票占比达到该比例则教案通过，可通过后发布到公共库（新建教研组默认带入）</div>
            </div>
            <div class="tr-rule-control">
              <el-input-number
                v-model="form.votePassPercent"
                :min="1"
                :max="100"
                size="small"
                controls-position="right"
              />
              <span class="tr-unit">%</span>
            </div>
          </div>

          <div class="tr-rule-item">
            <div class="tr-rule-main">
              <div class="tr-rule-title">通过后锁定</div>
              <div class="tr-rule-tip">
                开启（默认）：已通过 / 已入库后，禁止再提交新的 2.0 修订并重新投票。
                关闭：允许操作人用新的 2.0 替换并重开一轮投票。
              </div>
            </div>
            <div class="tr-rule-control">
              <el-switch v-model="form.lockAfterPass" active-text="开启" inactive-text="关闭" />
            </div>
          </div>

          <div class="tr-rule-item">
            <div class="tr-rule-main">
              <div class="tr-rule-title">默认有效评语字数</div>
              <div class="tr-rule-tip">新建教研组时的默认有效评语字数阈值</div>
            </div>
            <div class="tr-rule-control">
              <el-input-number
                v-model="form.defaultCommentThreshold"
                :min="0"
                :max="1000"
                size="small"
                controls-position="right"
              />
              <span class="tr-unit">字</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  </section>
</template>

<script>
import DepartmentPane from "@/components/DepartmentPane";
import { getButtonList } from "@/promissionRouter";
import { getPrepSchoolConfig, savePrepSchoolConfig } from "@/api/modules/teaching-research";
import { normalizeDeptId, treeNodeDeptId } from "@/utils/deptCascaderProps";

const schoolLikeTypes = ["school", "campus", "affiliateschool"];

function emptyForm() {
  return {
    votePassPercent: 60,
    lockAfterPass: true,
    defaultCommentThreshold: 15
  };
}

function pickSchoolDisplayName(node, data) {
  let n = node;
  while (n && n.data) {
    const t = String(n.data.DeptOrgType != null ? n.data.DeptOrgType : n.data.deptOrgType || "").toLowerCase();
    if (schoolLikeTypes.includes(t)) {
      return n.data.label || n.data.name || "";
    }
    n = n.parent;
  }
  return (data && (data.label || data.name)) || "";
}

/** 从树节点向上解析学校/校区 Id（字符串，避免雪花精度丢失）。最后修改：2026-10-18 */
function resolveSchoolId(node, fallback) {
  let n = node;
  while (n && n.data) {
    const t = String(n.data.DeptOrgType != null ? n.data.DeptOrgType : n.data.deptOrgType || "").toLowerCase();
    if (schoolLikeTypes.includes(t)) {
      return treeNodeDeptId(n.data) || normalizeDeptId(fallback);
    }
    n = n.parent;
  }
  return normalizeDeptId(fallback);
}

export default {
  name: "PrepConfig",
  components: { DepartmentPane },
  data() {
    return {
      buttonList: [],
      schoolId: "",
      currentSchoolName: "",
      form: emptyForm(),
      loading: false,
      saving: false
    };
  },
  computed: {
    canSave() {
      return (this.buttonList || []).some(b => b.Func === "savePrepConfig" || b.Func === "handleEdit" || b.Func === "handleAdd");
    }
  },
  mounted() {
    const routers = window.localStorage.router ? JSON.parse(window.localStorage.router) : [];
    this.buttonList = getButtonList(this.$route.path, routers);
  },
  methods: {
    treeNodeClick(data, node) {
      this.schoolId = resolveSchoolId(node, treeNodeDeptId(data));
      this.currentSchoolName = pickSchoolDisplayName(node, data);
      this.loadConfig();
    },
    async loadConfig() {
      if (!this.schoolId) {
        this.form = emptyForm();
        return;
      }
      this.loading = true;
      try {
        const res = await getPrepSchoolConfig({ schoolId: this.schoolId });
        if (res.success && res.response) {
          const r = res.response;
          this.form = {
            votePassPercent: r.VotePassPercent != null ? r.VotePassPercent : (r.votePassPercent != null ? r.votePassPercent : 60),
            lockAfterPass: r.LockAfterPass != null ? !!r.LockAfterPass : (r.lockAfterPass != null ? !!r.lockAfterPass : true),
            defaultCommentThreshold: r.DefaultCommentThreshold != null
              ? r.DefaultCommentThreshold
              : (r.defaultCommentThreshold != null ? r.defaultCommentThreshold : 15)
          };
        } else {
          this.$message.error(res.msg || "获取配置失败");
        }
      } finally {
        this.loading = false;
      }
    },
    async saveConfig() {
      if (!this.schoolId) {
        this.$message.error("请先选择学校");
        return;
      }
      if (!this.canSave) {
        this.$message.error("无保存权限");
        return;
      }
      this.saving = true;
      try {
        const res = await savePrepSchoolConfig({
          SchoolId: this.schoolId,
          VotePassPercent: this.form.votePassPercent,
          LockAfterPass: this.form.lockAfterPass,
          DefaultCommentThreshold: this.form.defaultCommentThreshold
        });
        if (res.success) {
          this.$message.success(res.msg || "保存成功");
        } else {
          this.$message.error(res.msg || "保存失败");
        }
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.prep-config-page .config-page-right {
  min-width: 0;
  overflow-x: hidden;
  box-sizing: border-box;
}

.prep-config-page .tr-panel {
  clear: both;
  width: 100%;
  max-width: 860px;
  box-sizing: border-box;
  padding: 16px 20px 8px;
}

.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f5f9;
}

.card-head__title {
  flex: 0 0 auto;
  white-space: nowrap;
  font-weight: 700;
  color: #0f172a;
  font-size: 15px;
}

.prep-config-page /deep/ .tr-rule-item {
  align-items: center;
}

.prep-config-page /deep/ .tr-rule-main {
  flex: 1;
  min-width: 0;
}

.prep-config-page /deep/ .tr-rule-control {
  flex: 0 0 auto;
}

.tr-stat__school {
  font-size: 15px !important;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
}
</style>
